<template>
    <Overlay :show="runtimeState.isBusy">
        <b-alert
            v-if="runtimeState.isCompleted"
            variant="success"
            show
            :class="contentVisible ? 'mb-2' : 'mb-0'"
        >
            <div class="alert-body review-and-submit-step__success">
                <span>{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):success',
                    'Response submitted successfully.',
                ) }}</span>
            </div>
        </b-alert>

        <b-alert
            v-if="runtimeState.isCompleted && refreshError"
            variant="warning"
            show
            class="mb-2"
        >
            <div class="alert-body">
                {{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):refresh-failed',
                    'The response was sent, but the latest request status could not be loaded.',
                ) }}
            </div>
        </b-alert>

        <div v-if="contentVisible" class="review-and-submit-step">
            <WorkflowReviewOverview
                v-if="settings.showWorkflowReview === true && reviewContributions.length > 0"
                :contributions="reviewContributions"
                @edit="$emit('open-step', $event)"
            />

            <DeclarationDetailsSection
                v-if="settings.showDeclarationDetails !== false"
                :value="otherInformation"
                :disabled="submissionBusy"
                @input="changeOtherInformation"
            />

            <DeclarationApprovalForm
                v-if="showIdentityConfirmation"
                ref="approvalForm"
                class="mt-1"
                :approval="declarationApproval"
                :verifying="verifying"
                @state-change="approvalDraft = $event"
                @request-code="requestApprovalCode"
                @verify-code="verifyApprovalCode"
            />

            <div class="review-and-submit-step__confirmations mt-2">
                <b-form-checkbox
                    v-if="settings.requireAuthorization !== false"
                    v-model="authorized"
                    class="review-and-submit-step__confirmation"
                    :disabled="submissionBusy"
                >
                    {{ translate(
                        'Page(ReceivedRequest):Component(ReviewAndSubmit):authorization-confirmation',
                        'I confirm that I am authorised to submit this declaration and that the information provided is accurate to the best of my knowledge.',
                    ) }}
                </b-form-checkbox>

                <b-form-checkbox
                    v-if="settings.showInformationReuseConsent !== false"
                    v-model="allowInformationReuse"
                    class="review-and-submit-step__confirmation"
                    :disabled="submissionBusy"
                >
                    {{ translate(
                        'Page(ReceivedRequest):Component(ReviewAndSubmit):information-reuse-consent',
                        'Make this information available for other requests so Chemity can answer them automatically.',
                    ) }}
                </b-form-checkbox>
            </div>

            <div class="review-and-submit-step__submit mt-2">
                <div class="review-and-submit-step__submit-meta">
                    <span class="text-less-muted">{{ submitHint }}</span>
                    <span v-if="showSubmittedAt" class="text-less-muted">
                        {{ submittedAtLabel }}
                    </span>
                </div>
                <b-button
                    variant="primary"
                    :disabled="submitDisabled"
                    @click="submit"
                >
                    <b-spinner v-if="submissionBusy" small class="mr-50" />
                    {{ submitLabel }}
                </b-button>
            </div>
        </div>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BButton,
    BFormCheckbox,
    BSpinner,
} from 'bootstrap-vue'
import Overlay from '@core/components/common/Overlay.vue'
import dateRenderer from '@core/mixins/date-renderer'
import DeclarationApprovalForm from './components/DeclarationApprovalForm.vue'
import DeclarationDetailsSection from './components/DeclarationDetailsSection.vue'
import WorkflowReviewOverview from './components/WorkflowReviewOverview.vue'

export default {
    name: 'ReviewAndSubmitStep',
    mixins: [dateRenderer],
    components: {
        BAlert,
        BButton,
        BFormCheckbox,
        BSpinner,
        DeclarationApprovalForm,
        DeclarationDetailsSection,
        Overlay,
        WorkflowReviewOverview,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
        reviewContributions: { type: Array, default: () => [] },
    },
    data() {
        const replySubmission = this.context.replySubmission || {}
        const initializeSubmissionOptions = replySubmission.submissionOptionsInitialized !== true

        return {
            approvalDraft: { name: '', email: '', code: '', ready: false },
            authorized: initializeSubmissionOptions
                && this.settings.requireAuthorization !== false
                ? true
                : replySubmission.authorizedToSubmit === true,
            allowInformationReuse: initializeSubmissionOptions
                && this.settings.showInformationReuseConsent !== false
                ? true
                : replySubmission.allowInformationReuse === true,
            initializeSubmissionOptions,
            verifying: false,
        }
    },
    created() {
        if (!this.initializeSubmissionOptions) {
            return
        }

        const payload = { submissionOptionsInitialized: true }
        if (this.settings.requireAuthorization !== false) {
            payload.authorizedToSubmit = true
        }
        if (this.settings.showInformationReuseConsent !== false) {
            payload.allowInformationReuse = true
        }

        this.$emit('action', { type: 'submission-options.change', payload })
    },
    computed: {
        otherInformation() {
            return this.context.request.otherInformation || {}
        },
        declarationApproval() {
            return this.context.declarationApproval || {
                required: false,
                approved: false,
                identity: { name: '', email: '' },
            }
        },
        contentVisible() {
            return !this.runtimeState.isCompleted
                || this.settings.keepContentAfterSubmit === true
        },
        refreshError() {
            return this.context.replySubmission
                ? this.context.replySubmission.refreshError
                : null
        },
        replySubmittedAt() {
            const replyDetails = this.context.request.replyDetails || {}
            return replyDetails.sentAt || null
        },
        showSubmittedAt() {
            return this.settings.showSubmittedAt !== false && Boolean(this.replySubmittedAt)
        },
        submittedAtLabel() {
            if (!this.replySubmittedAt) {
                return ''
            }
            const params = {
                date: this.renderDate(this.replySubmittedAt),
                time: this.renderTime(this.replySubmittedAt),
            }

            return this.translate(
                'Page(ReceivedRequest):Component(Text):last-reply-date',
                `Last response: ${params.date} ${params.time}`,
                params,
            )
        },
        showIdentityConfirmation() {
            return this.settings.showIdentityConfirmation !== false
                && this.declarationApproval.required === true
        },
        validitySelected() {
            if (this.settings.validityRequired === false || this.settings.showDeclarationDetails === false) {
                return true
            }
            const validity = this.otherInformation.declarationValidity
            const month = validity && typeof validity === 'object' ? validity.month : validity
            return Number(month) > 0
        },
        approvalReady() {
            return !this.showIdentityConfirmation
                || this.declarationApproval.approved === true
        },
        authorizationReady() {
            return this.settings.requireAuthorization === false || this.authorized
        },
        isValid() {
            return this.validitySelected && this.approvalReady && this.authorizationReady
        },
        submissionBusy() {
            return this.runtimeState.isBusy || this.verifying
        },
        submitDisabled() {
            return this.submissionBusy
                || !this.isValid
                || (this.runtimeState.isCompleted && this.settings.allowResubmission !== true)
        },
        submitLabel() {
            return this.runtimeState.isCompleted
                ? this.translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):send-updated-response',
                    'Send updated response',
                )
                : this.translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):submit-declaration',
                    'Submit declaration',
                )
        },
        submitHint() {
            if (!this.validitySelected) {
                return this.translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):select-validity-hint',
                    'Select a declaration validity period to submit.',
                )
            }
            if (!this.approvalReady) {
                return this.translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):verify-email-hint',
                    'Request a verification code and enter it to submit.',
                )
            }
            if (!this.authorizationReady) {
                return this.translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):confirm-authorization-hint',
                    'Confirm that you are authorised to submit this declaration.',
                )
            }
            return this.translate(
                this.runtimeState.isCompleted
                    ? 'Page(ReceivedRequest):Component(ReviewAndSubmit):ready-to-resubmit-hint'
                    : 'Page(ReceivedRequest):Component(ReviewAndSubmit):ready-to-submit-hint',
                this.runtimeState.isCompleted
                    ? 'You can update the information and send another response.'
                    : 'Your declaration is ready to submit.',
            )
        },
    },
    watch: {
        isValid: {
            immediate: true,
            handler() {
                this.emitValidity()
            },
        },
        authorized(value) {
            this.changeSubmissionOption('authorizedToSubmit', value)
        },
        allowInformationReuse(value) {
            this.changeSubmissionOption('allowInformationReuse', value)
        },
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = this.$t(key, params)
            return translation && translation !== key ? translation : fallback
        },
        emitValidity() {
            this.$emit('validity-change', {
                isValid: this.isValid,
                errors: this.isValid ? [] : [{ code: 'REVIEW_AND_SUBMIT_INCOMPLETE' }],
            })
        },
        changeOtherInformation(otherInformation) {
            this.$emit('action', {
                type: 'other-information.change',
                payload: { otherInformation },
            })
        },
        requestApprovalCode(payload) {
            this.$emit('action', {
                type: 'approval.code.request',
                payload,
            })
        },
        verifyApprovalCode(code) {
            if (this.verifying || !String(code || '').trim()) {
                return
            }

            this.verifying = true
            this.$emit('action', {
                type: 'approval.code.verify',
                payload: {
                    code: String(code).trim(),
                    completionCallback: () => {
                        this.verifying = false
                    },
                    failureCallback: error => {
                        this.verifying = false
                        if (this.$refs.approvalForm) {
                            this.$refs.approvalForm.setVerificationError(error)
                        }
                    },
                },
            })
        },
        changeSubmissionOption(name, value) {
            this.$emit('action', {
                type: 'submission-options.change',
                payload: { [name]: value === true },
            })
        },
        submit() {
            if (this.submitDisabled) {
                return
            }

            this.$emit('execute-terminal')
        },
    },
}
</script>

<style lang="scss" scoped>
.review-and-submit-step {
    &__success {
        align-items: center;
        display: flex;
        gap: 1rem;
    }

    &__confirmations {
        display: flex;
        flex-direction: column;
        gap: .75rem;
        padding: 1rem 0;
    }

    &__confirmation {
        margin: 0;
        padding-left: 1.75rem;
    }

    &__submit {
        align-items: center;
        border-top: 1px solid #ebe9f1;
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        padding-top: 1rem;
    }

    &__submit-meta {
        display: flex;
        flex-direction: column;
        margin-right: auto;
        text-align: left;
    }
}

::v-deep .review-and-submit-step__confirmation .custom-control-label {
    line-height: 1.5rem;
    vertical-align: top;
}

::v-deep .review-and-submit-step__confirmation .custom-control-label::before,
::v-deep .review-and-submit-step__confirmation .custom-control-label::after {
    top: .2rem;
}

@media (max-width: 767.98px) {
    .review-and-submit-step__submit {
        align-items: stretch;
        flex-direction: column;
    }

    .review-and-submit-step__submit-meta {
        margin-right: 0;
    }
}
</style>
